import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SPEED_FILTER_OPTIONS } from "../constants/filters";
import { VEHICLE_LIST_BATCH_SIZE } from "../constants/tracking";
import { useTrackingVehicles } from "../hooks";
import { useTrackingStore } from "../store";
import type { Telemetry, VehicleListProps } from "../types";
import { formatDate, matchesVehicleSearch } from "../utils";
import { VehicleCard } from "./cards";
import { PositionsTripsModal } from "./positions-trips-modal";
import { SearchInput, SpeedFilters } from "./vehicle-list-filters";

export const VehicleList = ({
	onVehicleSelect,
	onRouteSelect,
	initialRouteVehicleId,
	initialTripIds,
}: VehicleListProps) => {
	const {
		selectedVehicleId,
		setSelectedVehicleId,
		setVehicleFilterId,
		clearRoute,
	} = useTrackingStore();
	const [searchTerm, setSearchTerm] = useState("");
	const [speedFilter, setSpeedFilter] = useState<number | undefined>(undefined);
	const [visibleCount, setVisibleCount] = useState(VEHICLE_LIST_BATCH_SIZE);
	const [routeModal, setRouteModal] = useState<{
		plate: string;
		vehicleId: number;
	} | null>(null);
	const [initialRouteHandled, setInitialRouteHandled] = useState(false);
	const sentinelRef = useRef<HTMLDivElement | null>(null);

	const handleRouteClick = useCallback(
		({ plate, vehicleId }: { plate: string; vehicleId: number }) => {
			setSelectedVehicleId(vehicleId);
			setVehicleFilterId(vehicleId);
			setRouteModal({ plate, vehicleId });
		},
		[setSelectedVehicleId, setVehicleFilterId]
	);

	const handleCloseRouteModal = useCallback(() => {
		setRouteModal(null);
		setSelectedVehicleId(null);
		setVehicleFilterId(null);
		clearRoute();
	}, [setSelectedVehicleId, setVehicleFilterId, clearRoute]);

	const { data: trackingData, isLoading, error } = useTrackingVehicles();
	const vehicles = useMemo(() => trackingData?.vehicles ?? [], [trackingData]);

	const speedFilterFn = useMemo(
		() =>
			SPEED_FILTER_OPTIONS.find((option) => option.id === speedFilter)
				?.filterFn,
		[speedFilter]
	);

	const filteredVehicles = useMemo(
		() =>
			vehicles.filter(
				(telemetry) =>
					matchesVehicleSearch(telemetry, searchTerm) &&
					(!speedFilterFn || speedFilterFn(telemetry))
			),
		[vehicles, searchTerm, speedFilterFn]
	);

	const filterKey = `${searchTerm}|${speedFilter ?? ""}`;
	const previousFilterKeyRef = useRef(filterKey);
	if (previousFilterKeyRef.current !== filterKey) {
		previousFilterKeyRef.current = filterKey;
		setVisibleCount(VEHICLE_LIST_BATCH_SIZE);
	}

	const visibleVehicles = filteredVehicles.slice(0, visibleCount);
	const hasMore = visibleCount < filteredVehicles.length;

	useEffect(() => {
		const sentinel = sentinelRef.current;
		if (!(sentinel && hasMore)) {
			return;
		}

		const observer = new IntersectionObserver(
			(entries) => {
				if (entries[0]?.isIntersecting) {
					setVisibleCount((prev) =>
						Math.min(prev + VEHICLE_LIST_BATCH_SIZE, filteredVehicles.length)
					);
				}
			},
			{ threshold: 0.1 }
		);

		observer.observe(sentinel);
		return () => observer.disconnect();
	}, [hasMore, filteredVehicles.length]);

	useEffect(() => {
		if (
			initialRouteVehicleId &&
			(initialTripIds?.length ?? 0) > 0 &&
			!initialRouteHandled
		) {
			setSelectedVehicleId(initialRouteVehicleId);
			setVehicleFilterId(initialRouteVehicleId);
			setRouteModal({ plate: "", vehicleId: initialRouteVehicleId });
			setInitialRouteHandled(true);
			return;
		}

		if (
			!initialRouteVehicleId ||
			initialRouteHandled ||
			vehicles.length === 0
		) {
			return;
		}
		const telemetry = vehicles.find(
			(item) => item.vehicleId === initialRouteVehicleId
		);
		if (!telemetry) {
			return;
		}
		setSelectedVehicleId(telemetry.vehicleId);
		setVehicleFilterId(telemetry.vehicleId);
		setRouteModal({ plate: telemetry.plate, vehicleId: telemetry.vehicleId });
		setInitialRouteHandled(true);
	}, [
		initialRouteVehicleId,
		initialTripIds?.length,
		initialRouteHandled,
		vehicles,
		setSelectedVehicleId,
		setVehicleFilterId,
	]);

	if (isLoading) {
		return (
			<div className="flex justify-center p-8">
				<div className="text-gray-500 text-sm">Carregando...</div>
			</div>
		);
	}

	if (error) {
		return (
			<div className="flex justify-center p-8">
				<div className="text-red-500 text-sm">Erro ao carregar veículos</div>
			</div>
		);
	}

	return (
		<div className="flex h-full min-h-0 flex-col gap-6">
			<div className="flex shrink-0 flex-col gap-6">
				<SearchInput onChange={setSearchTerm} value={searchTerm} />

				<SpeedFilters
					movingCount={trackingData?.moving ?? 0}
					setSpeedFilter={setSpeedFilter}
					speedFilter={speedFilter}
					stoppedCount={trackingData?.stopped ?? 0}
				/>
			</div>

			{filteredVehicles.length === 0 ? (
				<div className="flex justify-center p-8">
					<div className="text-gray-500 text-sm">Nenhum veículo encontrado</div>
				</div>
			) : (
				<div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto pb-4">
					{visibleVehicles.map((telemetry: Telemetry) => (
						<VehicleCard
							data={{
								latitude: telemetry.latitude,
								longitude: telemetry.longitude,
								moving: telemetry.moving,
								vehiclePlate: telemetry.plate,
								vehicleId: telemetry.vehicleId,
								lastUpdate: formatDate(telemetry.dateTelemetry),
								driver: telemetry.driver?.name || "N/A",
								address: telemetry.address ?? undefined,
								trailerPlates: (telemetry.trailers ?? []).map(
									(trailer) => trailer.plate
								),
							}}
							isSelected={selectedVehicleId === telemetry.vehicleId}
							key={telemetry.vehicleId}
							onClick={() => onVehicleSelect(telemetry)}
							onRouteClick={handleRouteClick}
						/>
					))}

					{hasMore ? (
						<div aria-hidden="true" className="h-1" ref={sentinelRef} />
					) : null}

					<span aria-live="polite" className="sr-only">
						{`${visibleVehicles.length} de ${filteredVehicles.length} veículos carregados`}
					</span>
				</div>
			)}

			<PositionsTripsModal
				initialTripIds={initialTripIds}
				isOpen={routeModal !== null}
				onClose={handleCloseRouteModal}
				onRouteSelect={onRouteSelect}
				vehicleId={routeModal?.vehicleId ?? 0}
				vehiclePlate={routeModal?.plate ?? ""}
			/>
		</div>
	);
};
